import { useState, useCallback } from "react";
import type {
  ChatSessionResponseDto,
  ItineraryAction,
  PackingAction,
  PlaceRecommendation,
} from "@tabipla/shared";
import { useSendChatMessageWithAutoSession } from "./useTrips";
import { useChatUI, useChatAutoScroll } from "./useChatUI";
import { useChatSession } from "./useChatSession";
import { useErrorHandler } from "./useErrorHandler";

export interface MessageActions {
  actions?: ItineraryAction[];
  packingActions?: PackingAction[];
  placeRecommendations?: PlaceRecommendation[];
}

export interface UseTripChatReturn {
  // UI State
  showChat: boolean;
  isChatFullScreen: boolean;
  chatInput: string;
  isSending: boolean;

  // Session State
  currentSessionId: string | null;
  showSessionDropdown: boolean;

  // Data
  sessions: ChatSessionResponseDto[] | undefined;
  chatMessages: ReturnType<typeof useChatSession>["chatMessages"];
  messageActions: Record<string, MessageActions>;

  // Refs
  messagesEndRef: React.RefObject<HTMLDivElement>;

  // UI Actions
  setShowChat: (show: boolean) => void;
  setIsChatFullScreen: (fullScreen: boolean) => void;
  setChatInput: (input: string) => void;
  closeChat: () => void;

  // Session Actions
  setShowSessionDropdown: (show: boolean) => void;
  handleNewSession: () => void;
  handleSelectSession: (session: ChatSessionResponseDto) => void;
  handleDeleteSession: (sessionId: string, e: React.MouseEvent) => Promise<void>;

  // Message Actions
  handleSendMessage: (e: React.FormEvent) => Promise<void>;

  // Confirm dialog
  dialogProps: ReturnType<typeof useChatSession>["dialogProps"];
}

/**
 * Composite hook for trip chat functionality.
 * Combines useChatUI and useChatSession with message sending logic.
 */
export function useTripChat(tripId: string | undefined): UseTripChatReturn {
  const { handleError } = useErrorHandler();

  // Compose UI and Session hooks
  const ui = useChatUI();
  const session = useChatSession(tripId);

  // Message actions state (for AI response badges)
  const [messageActions, setMessageActions] = useState<Record<string, MessageActions>>({});

  // Message sending mutation
  const sendMessageMutation = useSendChatMessageWithAutoSession(tripId ?? "");

  // Auto-scroll when messages change
  useChatAutoScroll(ui.messagesEndRef, session.chatMessages);

  const handleSendMessage = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!ui.chatInput.trim() || !tripId) return;

      const message = ui.chatInput;
      ui.clearInput();
      ui.setIsSending(true);

      try {
        const result = await sendMessageMutation.mutateAsync({
          message,
          sessionId: session.currentSessionId,
        });

        // Update session ID if a new session was created
        if (!session.currentSessionId && result.sessionId) {
          session.setCurrentSessionId(result.sessionId);
          session.setIsStartingNewSession(false);
        }

        // Store actions and place recommendations for the assistant message
        if (result.response?.assistantMessage) {
          const { actions, packingActions, placeRecommendations } = result.response;
          if (
            (actions && actions.length > 0) ||
            (packingActions && packingActions.length > 0) ||
            (placeRecommendations && placeRecommendations.length > 0)
          ) {
            setMessageActions((prev) => ({
              ...prev,
              [result.response!.assistantMessage.id]: {
                actions,
                packingActions,
                placeRecommendations,
              },
            }));
          }
        }
      } catch (error) {
        handleError(error, { message: "メッセージの送信に失敗しました" });
        ui.restoreInput(message); // Restore the message on error
      } finally {
        ui.setIsSending(false);
      }
    },
    [ui, tripId, session, sendMessageMutation, handleError]
  );

  return {
    // UI State
    showChat: ui.showChat,
    isChatFullScreen: ui.isChatFullScreen,
    chatInput: ui.chatInput,
    isSending: ui.isSending,

    // Session State
    currentSessionId: session.currentSessionId,
    showSessionDropdown: session.showSessionDropdown,

    // Data
    sessions: session.sessions,
    chatMessages: session.chatMessages,
    messageActions,

    // Refs
    messagesEndRef: ui.messagesEndRef,

    // UI Actions
    setShowChat: ui.setShowChat,
    setIsChatFullScreen: ui.setIsChatFullScreen,
    setChatInput: ui.setChatInput,
    closeChat: ui.closeChat,

    // Session Actions
    setShowSessionDropdown: session.setShowSessionDropdown,
    handleNewSession: session.handleNewSession,
    handleSelectSession: session.handleSelectSession,
    handleDeleteSession: session.handleDeleteSession,

    // Message Actions
    handleSendMessage,

    // Confirm dialog
    dialogProps: session.dialogProps,
  };
}
